import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface DocumentationSection {
  id: string;
  title: string;
  description: string;
  subsections: {
    id: string;
    title: string;
    content: string;
    steps?: string[];
  }[];
}

const Documentation: React.FC = () => {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState<string>('getting-started');
  const [searchQuery, setSearchQuery] = useState('');

  const documentationSections: DocumentationSection[] = [
    {
      id: 'getting-started',
      title: 'Getting Started',
      description: 'Learn how to set up your account and navigate the platform',
      subsections: [
        {
          id: 'account-creation',
          title: 'Creating Your Account',
          content: 'Follow these steps to create your NexSync account and start using our platform.',
          steps: [
            'Click the "Sign Up" button on the homepage',
            'Select your role (Student, Freelancer, or Client)',
            'Fill in your personal information',
            'Verify your email address',
            'Complete your profile setup',
            'Set up your payment method (for freelancers)'
          ]
        },
        {
          id: 'profile-setup',
          title: 'Profile Setup Guide',
          content: 'A complete profile increases your credibility and helps us match you with the right projects or freelancers.',
          steps: [
            'Upload a professional profile picture',
            'Complete your bio and expertise',
            'Add your educational background',
            'Upload portfolio samples',
            'Set your availability and rates',
            'Verify your identity documents'
          ]
        },
        {
          id: 'platform-navigation',
          title: 'Navigating the Platform',
          content: 'Learn how to use the main features and navigate through the platform efficiently.'
        }
      ]
    },
    {
      id: 'students-guide',
      title: 'For Students',
      description: 'Complete guide for students posting and managing projects',
      subsections: [
        {
          id: 'posting-projects',
          title: 'How to Post a Project',
          content: 'Create a project request that attracts the best freelancers.',
          steps: [
            'Click "Post Project" on your dashboard',
            'Select project category and type',
            'Provide detailed requirements',
            'Set your budget and timeline',
            'Upload any reference materials',
            'Review and publish your project'
          ]
        },
        {
          id: 'selecting-freelancers',
          title: 'Selecting the Right Freelancer',
          content: 'Tips for choosing the best freelancer for your project needs.'
        },
        {
          id: 'project-management',
          title: 'Managing Your Project',
          content: 'How to communicate with freelancers and track project progress.'
        },
        {
          id: 'payment-release',
          title: 'Releasing Payments',
          content: 'Guide to our escrow system and how to release payments upon project completion.'
        }
      ]
    },
    {
      id: 'freelancers-guide',
      title: 'For Freelancers',
      description: 'Guide for freelancers to find and complete projects',
      subsections: [
        {
          id: 'finding-projects',
          title: 'Finding Suitable Projects',
          content: 'How to search and apply for projects that match your skills.'
        },
        {
          id: 'proposal-writing',
          title: 'Writing Winning Proposals',
          content: 'Tips for creating proposals that stand out and win projects.'
        },
        {
          id: 'delivery-standards',
          title: 'Project Delivery Standards',
          content: 'Quality standards and expectations for project delivery.'
        },
        {
          id: 'payment-withdrawal',
          title: 'Withdrawing Your Earnings',
          content: 'How to set up and use the payment withdrawal system.'
        }
      ]
    },
    {
      id: 'technical',
      title: 'Technical Documentation',
      description: 'API references and technical guides',
      subsections: [
        {
          id: 'api-overview',
          title: 'API Overview',
          content: 'Introduction to the NexSync API for developers.'
        },
        {
          id: 'authentication',
          title: 'Authentication',
          content: 'How to authenticate with our API using OAuth 2.0.'
        },
        {
          id: 'webhooks',
          title: 'Webhooks',
          content: 'Setting up and using webhooks for real-time notifications.'
        },
        {
          id: 'rate-limits',
          title: 'Rate Limits',
          content: 'Understanding API rate limits and best practices.'
        }
      ]
    },
    {
      id: 'security',
      title: 'Security & Privacy',
      description: 'Security measures and privacy controls',
      subsections: [
        {
          id: 'data-protection',
          title: 'Data Protection',
          content: 'How we protect your data and ensure privacy.'
        },
        {
          id: 'two-factor-auth',
          title: 'Two-Factor Authentication',
          content: 'Setting up and using 2FA for enhanced account security.'
        },
        {
          id: 'secure-communications',
          title: 'Secure Communications',
          content: 'Our end-to-end encryption for all platform communications.'
        }
      ]
    }
  ];

  const activeDoc = documentationSections.find(doc => doc.id === activeSection);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation */}
      <nav className="navbar">
        <div className="navbar-container">
          <div className="navbar-brand">
            <div className="brand-logo">NS</div>
            <span className="brand-text">NexSync</span>
          </div>
          <div className="navbar-actions">
            <button 
              onClick={() => navigate('/')}
              className="btn btn-ghost"
            >
              Home
            </button>
            <button 
              onClick={() => navigate('/signin')}
              className="btn btn-ghost"
            >
              Sign in
            </button>
            <button 
              onClick={() => navigate('/signup')}
              className="btn btn-primary"
            >
              Sign up
            </button>
          </div>
        </div>
      </nav>

      {/* Documentation Content */}
      <div className="container mx-auto px-4 py-8 max-w-7xl">
        <div className="grid lg:grid-cols-4 gap-8">
          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="sticky top-24">
              {/* Search */}
              <div className="mb-6">
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search documentation..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full px-4 py-2 pl-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <svg className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
              </div>

              {/* Navigation */}
              <nav className="space-y-1">
                {documentationSections.map((section) => (
                  <div key={section.id} className="mb-2">
                    <button
                      onClick={() => setActiveSection(section.id)}
                      className={`w-full text-left px-4 py-3 rounded-lg transition-colors ${
                        activeSection === section.id
                          ? 'bg-blue-600 text-white'
                          : 'text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      <div className="font-medium">{section.title}</div>
                      <div className={`text-sm mt-1 ${
                        activeSection === section.id ? 'text-blue-100' : 'text-gray-500'
                      }`}>
                        {section.description}
                      </div>
                    </button>
                    
                    {activeSection === section.id && section.subsections && (
                      <div className="ml-4 mt-2 space-y-1 border-l border-gray-200 pl-3">
                        {section.subsections.map((subsection) => (
                          <a
                            key={subsection.id}
                            href={`#${subsection.id}`}
                            className="block py-2 text-sm text-gray-600 hover:text-blue-600"
                          >
                            {subsection.title}
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </nav>

              {/* Quick Links */}
              <div className="mt-8 p-4 bg-gray-50 rounded-lg">
                <h4 className="font-medium text-gray-900 mb-3">Quick Links</h4>
                <div className="space-y-2">
                  <button 
                    onClick={() => navigate('/helpcenter')}
                    className="block text-sm text-blue-600 hover:text-blue-700"
                  >
                    Help Center
                  </button>
                  <button 
                    onClick={() => navigate('/contactus')}
                    className="block text-sm text-blue-600 hover:text-blue-700"
                  >
                    Contact Support
                  </button>
                  <button 
                    onClick={() => navigate('/termsofservice')}
                    className="block text-sm text-blue-600 hover:text-blue-700"
                  >
                    Terms of Service
                  </button>
                  <button 
                    onClick={() => navigate('/privacypolicy')}
                    className="block text-sm text-blue-600 hover:text-blue-700"
                  >
                    Privacy Policy
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            {activeDoc ? (
              <div className="bg-white rounded-xl shadow-md p-8">
                <div className="mb-8">
                  <h1 className="text-3xl font-bold text-gray-900 mb-2">{activeDoc.title}</h1>
                  <p className="text-lg text-gray-600">{activeDoc.description}</p>
                </div>

                <div className="space-y-12">
                  {activeDoc.subsections.map((subsection, index) => (
                    <section key={subsection.id} id={subsection.id} className="scroll-mt-24">
                      <div className="flex items-start gap-4 mb-6">
                        <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                          <span className="text-blue-600 font-bold">{index + 1}</span>
                        </div>
                        <div>
                          <h2 className="text-2xl font-bold text-gray-900 mb-3">{subsection.title}</h2>
                          <p className="text-gray-700 mb-6">{subsection.content}</p>
                          
                          {subsection.steps && (
                            <div className="bg-gray-50 rounded-lg p-6 mb-6">
                              <h3 className="font-semibold text-gray-900 mb-4">Step-by-Step Guide:</h3>
                              <ol className="space-y-3">
                                {subsection.steps.map((step, stepIndex) => (
                                  <li key={stepIndex} className="flex items-start gap-3">
                                    <span className="flex-shrink-0 w-6 h-6 bg-blue-600 text-white rounded-full text-sm flex items-center justify-center">
                                      {stepIndex + 1}
                                    </span>
                                    <span className="text-gray-700">{step}</span>
                                  </li>
                                ))}
                              </ol>
                            </div>
                          )}

                          {/* Example or Code Snippet */}
                          {subsection.id.includes('api') && (
                            <div className="bg-gray-800 rounded-lg p-6 mb-6">
                              <div className="flex justify-between items-center mb-4">
                                <span className="text-gray-300 font-mono text-sm">Example Request</span>
                                <button className="text-gray-400 hover:text-white text-sm">
                                  Copy
                                </button>
                              </div>
                              <pre className="text-gray-300 overflow-x-auto">
                                <code>
{`curl -X POST https://api.nexsync.com/v1/projects \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "title": "Project Title",
    "description": "Project description",
    "budget": 50000,
    "deadline": "2024-12-31"
  }'`}
                                </code>
                              </pre>
                            </div>
                          )}

                          {/* Related Articles */}
                          <div className="border-t border-gray-200 pt-6 mt-6">
                            <h4 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-3">
                              Related Articles
                            </h4>
                            <div className="grid md:grid-cols-2 gap-4">
                              {activeDoc.subsections
                                .filter(s => s.id !== subsection.id)
                                .slice(0, 2)
                                .map(related => (
                                  <a
                                    key={related.id}
                                    href={`#${related.id}`}
                                    className="block p-3 border border-gray-200 rounded-lg hover:border-blue-300 hover:bg-blue-50 transition-colors"
                                  >
                                    <div className="font-medium text-gray-900">{related.title}</div>
                                    <div className="text-sm text-gray-600 mt-1">
                                      {related.content.substring(0, 80)}...
                                    </div>
                                  </a>
                                ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    </section>
                  ))}
                </div>

                {/* Feedback Section */}
                <div className="mt-12 pt-8 border-t border-gray-200">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                      <h3 className="text-lg font-medium text-gray-900 mb-1">Was this helpful?</h3>
                      <p className="text-gray-600">Let us know if this documentation helped you.</p>
                    </div>
                    <div className="flex gap-3">
                      <button className="px-4 py-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200">
                        👍 Yes, it helped
                      </button>
                      <button className="px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200">
                        👎 Needs improvement
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-xl shadow-md p-8 text-center">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Documentation Not Found</h3>
                <p className="text-gray-600 mb-6">Select a section from the sidebar to view documentation.</p>
                <button 
                  onClick={() => navigate('/helpcenter')}
                  className="btn btn-primary"
                >
                  Visit Help Center
                </button>
              </div>
            )}

            {/* Table of Contents for Mobile */}
            <div className="lg:hidden mt-8">
              <details className="bg-white rounded-xl shadow-md">
                <summary className="px-6 py-4 font-medium text-gray-900 cursor-pointer">
                  Table of Contents
                </summary>
                <div className="px-6 pb-4 border-t border-gray-200">
                  <nav className="space-y-2">
                    {documentationSections.map((section) => (
                      <div key={section.id} className="pt-2">
                        <button
                          onClick={() => setActiveSection(section.id)}
                          className={`block w-full text-left font-medium ${
                            activeSection === section.id ? 'text-blue-600' : 'text-gray-700'
                          }`}
                        >
                          {section.title}
                        </button>
                        {activeSection === section.id && (
                          <div className="ml-3 mt-2 space-y-1">
                            {section.subsections.map((subsection) => (
                              <a
                                key={subsection.id}
                                href={`#${subsection.id}`}
                                className="block py-1 text-sm text-gray-600 hover:text-blue-600"
                              >
                                {subsection.title}
                              </a>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </nav>
                </div>
              </details>
            </div>

            {/* Next Steps */}
            <div className="mt-8 grid md:grid-cols-2 gap-6">
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Need More Help?</h3>
                <p className="text-gray-700 mb-4">
                  Can't find what you're looking for? Our support team is ready to assist you.
                </p>
                <button 
                  onClick={() => navigate('/contactus')}
                  className="btn btn-primary"
                >
                  Contact Support
                </button>
              </div>

              <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Community Forum</h3>
                <p className="text-gray-700 mb-4">
                  Join discussions with other users and share knowledge in our community forum.
                </p>
                <button 
                  onClick={() => window.open('https://community.nexsync.com', '_blank')}
                  className="btn btn-success"
                >
                  Visit Forum
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-container">
          <div className="footer-content">
            <div className="footer-brand">
              <div className="navbar-brand">
                <div className="brand-logo">NS</div>
                <span className="brand-text">NexSync</span>
              </div>
              <p className="footer-description">
                Comprehensive documentation for all your needs.
              </p>
            </div>
            
            <div className="footer-links">
              <div className="footer-column">
                <h4>Resources</h4>
                <button onClick={() => navigate('/documentation')} className="footer-link">Documentation</button>
                <button onClick={() => navigate('/helpcenter')} className="footer-link">Help Center</button>
                <button onClick={() => navigate('/blog')} className="footer-link">Blog</button>
              </div>
              
              <div className="footer-column">
                <h4>Legal</h4>
                <button onClick={() => navigate('/privacypolicy')} className="footer-link">Privacy Policy</button>
                <button onClick={() => navigate('/termsofservice')} className="footer-link">Terms of Service</button>
                <button onClick={() => navigate('/cookiepolicy')} className="footer-link">Cookie Policy</button>
              </div>
            </div>
          </div>
          
          <div className="footer-bottom">
            <p>&copy; {new Date().getFullYear()} NexSync. All rights reserved.</p>
            <p className="text-sm text-gray-500 mt-2">Documentation last updated: November 2024</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Documentation;